import { Injectable, ElementRef } from '@angular/core';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import {
	BehaviorSubject,
	Subject,
	Observable,
	combineLatest,
	of,
	defer,
	EMPTY,
	throwError,
} from 'rxjs';
import {
	catchError,
	debounceTime,
	distinctUntilChanged,
	exhaustMap,
	filter,
	finalize,
	map,
	shareReplay,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs/operators';

import {
	BodyType,
	CollectionItem,
	ExecuteResponseVm,
	HistoryItem,
	HttpMethod,
	ItCollectionDto,
	Kv,
	PathNode,
	RequestItem,
	ResponseTab,
	SubTab,
	ThemeMode,
	Vm,
} from './integration-tester.models';

import {
	IT_LAST_INTEGRATION_KEY,
	IT_RESP_WIDTH_KEY,
	IT_THEME_KEY,
	IT_TEMPLATES_COLLECTION_NAME,
	IT_TEMPLATES_ID_PREFIX,
	keyHistory,
	keyLastCollection,
	keyVars,
} from './integration-tester.tokens';

import {
	clamp,
	deepClone,
	escapeHtml,
	getByPath,
	headersDictToText,
	httpHeadersToDict,
	isJsonLike,
	isXmlLike,
	mergeKv,
	newId,
	newTreeId,
	normalizeError,
	prettyXml,
	safeNumber,
} from './integration-tester.util';

import { AsamcoUserService } from '@asamco/base';
import { ApplicationService } from '../../services/application.service';
import {
	IntegrationTesterAdminService,
	SendEnvelopeDto,
	SendRequestDto,
} from '../../services/integration-tester-admin.service';
import { ResponseRenderService } from '../response-render.service';

@Injectable({ providedIn: 'root' })
export class IntegrationTesterFacade {
	private readonly destroy$ = new Subject<void>();

	// --------------------
	// State
	// --------------------
	private readonly selectedIntegrationId$ = new BehaviorSubject<number | null>(null);
	private readonly selectedLoginId$ = new BehaviorSubject<number | null>(null);

	private readonly selectedTemplateId$ = new BehaviorSubject<string | null>(null);

	private readonly collections$ = new BehaviorSubject<CollectionItem[]>([]);
	private readonly activeCollectionId$ = new BehaviorSubject<string | null>(null);
	private readonly activeRequestId$ = new BehaviorSubject<string | null>(null);

	private readonly requestSearch$ = new BehaviorSubject<string>('');
	private readonly customVars$ = new BehaviorSubject<Kv[]>([]);

	private readonly subTab$ = new BehaviorSubject<SubTab>('params');
	private readonly responseTab$ = new BehaviorSubject<ResponseTab>('body');

	private readonly isSending$ = new BehaviorSubject<boolean>(false);
	private readonly lastResponse$ = new BehaviorSubject<ExecuteResponseVm | null>(null);

	private readonly history$ = new BehaviorSubject<HistoryItem[]>([]);
	private readonly error$ = new BehaviorSubject<string | null>(null);

	private readonly findQuery$ = new BehaviorSubject<string>('');
	private readonly findIndex$ = new BehaviorSubject<number>(0);

	private readonly gridPath$ = new BehaviorSubject<string>('');
	private readonly gridError$ = new BehaviorSubject<string | null>(null);

	private readonly isTestingLogin$ = new BehaviorSubject<boolean>(false);

	private readonly themeSubject = new BehaviorSubject<ThemeMode>('dark');
	readonly theme$ = this.themeSubject.asObservable();

	// Response panel width (CSS var is set by shell via binding; we just persist + expose value if you want)
	private readonly respWidth$ = new BehaviorSubject<number>(560);

	private readonly MAX_RENDER_CHARS = 250_000; // tune
	private readonly MAX_MATCHES = 2000;

	private xmlFindCache: {
		key: string;          // body signature + query
		pretty: string;       // pretty xml text
		htmlNoActive: string; // escaped html with <mark class="it-mark" data-mi="..."> but NO is-active
		matches: number;
	} | null = null;

	// --------------------
	// External data
	// --------------------
	readonly isAdmin$: Observable<boolean> = defer(() => this.asamcoUserService.isAdmin()).pipe(
		catchError(err => {
			this.error$.next(normalizeError(err));
			return of(false);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly integrations$ = this.applicationService.get().pipe(
		map(list => (list ?? []).slice().sort((a, b) => (a.name > b.name ? 1 : -1))),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly adminLogins$ = this.selectedIntegrationId$.pipe(
		distinctUntilChanged(),
		switchMap(id => (id ? this.adminService.getAdminLogins(id) : of([]))),
		catchError(err => {
			this.error$.next(normalizeError(err));
			return of([]);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	// Templates derived from templates collection
	readonly templates$: Observable<Array<{ id: string; name: string }>> = this.collections$.pipe(
		map(cols => {
			const tcol = this.findTemplatesCollection(cols);
			return (tcol?.requests ?? []).map(r => ({ id: r.id, name: r.name }));
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	// Parse response body (json or xml->obj)
	private readonly MAX_PARSE_CHARS = 2_000_000;

	private readonly parsedBody$ = this.lastResponse$.pipe(
		map(r => (r?.bodyText ?? '').toString().trim()),
		distinctUntilChanged(),
		map(txt => {
			if (!txt) return null;
			if (txt.length > this.MAX_PARSE_CHARS) return null; // guard
			if (isJsonLike(txt)) { try { return JSON.parse(txt); } catch { return null; } }
			if (isXmlLike(txt)) { try { return this.parseXmlToObject(txt); } catch { return null; } }
			return null;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly pathTreeItems$: Observable<PathNode[]> = this.parsedBody$.pipe(
		map(obj => this.buildPathTree(obj)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	readonly gridRows$ = combineLatest([
		this.parsedBody$,
		this.gridPath$.pipe(
			map(x => (x ?? '').toString()),
			debounceTime(200),
			distinctUntilChanged()
		),
	]).pipe(
		map(([obj, path]) => {
			this.gridError$.next(null);
			if (!obj) return null;

			const resolved = getByPath(obj, path);

			if (Array.isArray(resolved)) return this.stringifyNestedRows(resolved);
			if (resolved && typeof resolved === 'object') return this.stringifyNestedRows([resolved]);

			if ((path ?? '').trim().length === 0) {
				this.gridError$.next('Enter a JSON path (example: d.results).');
				return null;
			}

			this.gridError$.next(`Path "${path}" did not resolve to an array/object.`);
			return null;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	// Pretty response + highlighting (HTML string)
	private readonly prettyBodyText$ = this.lastResponse$.pipe(
		map(r => (r?.bodyText ?? '').toString()),
		distinctUntilChanged(),
		map(text => {
			const trimmed = text.trim();
			if (!trimmed) return { text: '', kind: 'text' as const };

			if (isJsonLike(trimmed)) {
				try {
					return { text: JSON.stringify(JSON.parse(trimmed), null, 2), kind: 'json' as const };
				} catch { /* fall through */ }
			}

			if (isXmlLike(trimmed)) {
				return { text: prettyXml(trimmed), kind: 'xml' as const };
			}

			return { text, kind: 'text' as const };
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private tryPrettyBody(text: string): { text: string; kind: 'json' | 'xml' | 'text' } {
		const t = (text ?? '').toString();
		const trimmed = t.trim();
		if (!trimmed) return { text: '', kind: 'text' };

		if (isJsonLike(trimmed)) {
			try {
				const obj = JSON.parse(trimmed);
				return { text: JSON.stringify(obj, null, 2), kind: 'json' };
			} catch { }
		}

		if (isXmlLike(trimmed)) {
			try {
				return { text: prettyXml(trimmed), kind: 'xml' };
			} catch { }
		}

		return { text: t, kind: 'text' };
	}

	private tryPrettyText(text: string): { text: string } {
		const t = (text ?? '').toString();
		const trimmed = t.trim();
		if (!trimmed) return { text: '' };

		if (isJsonLike(trimmed)) {
			try {
				const obj = JSON.parse(trimmed);
				return { text: JSON.stringify(obj, null, 2) };
			} catch { /* fall through */ }
		}

		if (isXmlLike(trimmed)) {
			try {
				return { text: prettyXml(trimmed) }; // you already import prettyXml
			} catch { /* fall through */ }
		}

		return { text: t };
	}

	private readonly HUGE_THRESHOLD = 250_000; // chars; tune

	readonly renderedBodyHtml$ = combineLatest([
		this.lastResponse$.pipe(map(r => (r?.bodyText ?? '').toString()), distinctUntilChanged()),
		this.findQuery$.pipe(
			map(q => (q ?? '').toString().trim()),
			debounceTime(120),
			distinctUntilChanged()
		),
		this.findIndex$,
		this.responseTab$.pipe(distinctUntilChanged()),
	]).pipe(
		switchMap(([bodyText, q, idx, tab]) => {
			if (tab !== 'body' || !bodyText) {
				return of({
					html: '',
					matches: 0,
					index: 0,
					lineCount: 1,
					kind: 'text' as const,
					isHuge: false,
					rawText: '',
				});
			}

			// IMPORTANT: pretty first
			const pretty = this.tryPrettyBody(bodyText);
			const textToRender = pretty.text;
			const kind = pretty.kind;

			const isHuge = textToRender.length > this.HUGE_THRESHOLD;

			// HUGE MODE: no HTML; virtual scroll renders rawText (already pretty if xml/json)
			if (isHuge) {
				return of({
					html: '',
					matches: 0,
					index: 0,
					lineCount: 1,
					kind,
					isHuge: true,
					rawText: textToRender,
				});
			}

			// NORMAL MODE: highlight/render HTML from pretty text
			const formatted =
				bodyText.trim().startsWith('<')
					? prettyXml(bodyText)
					: bodyText;

			const trimmed = (bodyText ?? '').toString().trim();
			if (isXmlLike(trimmed)) {
				const pretty = prettyXml(trimmed);
				const lc = pretty ? pretty.split('\n').length : 1;

				const q2 = (q ?? '').toString().trim();
				const canHighlight = q2.length >= 2;

				if (!canHighlight) {
					// reset cache so we don't reuse old marks when query cleared
					this.xmlFindCache = null;

					return of({
						html: this.preserveIndentAndBr(this.escapeHtml(pretty)),
						matches: 0,
						index: 0,
						lineCount: lc,
						kind: 'xml' as const,
						isHuge: false,
						rawText: '',
					});
				}

				// Keyed by XML + query (not index)
				const key = `${pretty.length}|${q2}|${pretty.slice(0, 128)}|${pretty.slice(-128)}`;

				if (!this.xmlFindCache || this.xmlFindCache.key !== key) {
					// expensive path only when xml/query changes
					const base = this.highlightTextNoActive(pretty, q2, Math.min(this.MAX_MATCHES, 800)); // cap XML matches for speed
					this.xmlFindCache = {
						key,
						pretty,
						htmlNoActive: base.html,
						matches: base.matches,
					};
				}

				const matches = this.xmlFindCache.matches;
				const clampedIndex = matches ? Math.max(0, Math.min(idx, matches - 1)) : 0;

				// cheap path when only index changes
				const htmlActive = matches
					? this.setActiveMark(this.xmlFindCache.htmlNoActive, clampedIndex)
					: this.xmlFindCache.htmlNoActive;

				if (matches > 0 && clampedIndex !== idx) this.findIndex$.next(clampedIndex);

				return of({
					html: this.preserveIndentAndBr(htmlActive),
					matches,
					index: clampedIndex,
					lineCount: lc,
					kind: 'xml' as const,
					isHuge: false,
					rawText: '',
				});
			}

			return this.responseRender.render({
				text: formatted,
				query: q,
				index: idx,
				maxChars: this.MAX_RENDER_CHARS,
				maxMatches: this.MAX_MATCHES,
			}).pipe(
				tap(res => {
					if ((res.matches ?? 0) > 0 && res.index !== idx) this.findIndex$.next(res.index);
				}),
				map(res => ({
					html: res.html,
					matches: res.matches,
					index: res.index,
					lineCount: res.lineCount,
					kind: res.kind,
					isHuge: false,
					rawText: '',
				}))
			);
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private wrapAsLines(html: string): string {
		const lines = (html ?? '').split('\n');
		return lines
			.map(l => `<div class="it-resline">${l.length ? l : '&nbsp;'}</div>`)
			.join('');
	}


	setFindIndex(i: number): void {
		const n = Number(i);
		this.findIndex$.next(Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0);
	}

	clearFind(): void {
		this.findQuery$.next('');
		this.findIndex$.next(0);
	}

	// reuse your existing escapeHtml() in facade; if you don't have it there, keep this:
	private escapeHtml(s: string): string {
		return (s ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#39;');
	}

	// --------------------
	// VM
	// --------------------
	readonly vm$: Observable<Vm> = combineLatest([
		this.isAdmin$,
		this.integrations$,
		this.selectedIntegrationId$,
		this.adminLogins$,
		this.selectedLoginId$,
		this.templates$,
		this.selectedTemplateId$,
		this.collections$,
		this.activeCollectionId$,
		this.activeRequestId$,
		this.requestSearch$,
		this.customVars$,
		this.subTab$,
		this.isSending$,
		this.responseTab$,
		this.lastResponse$,
		this.history$,
		this.error$,
		this.gridPath$,
		this.gridRows$,
		this.gridError$,
		this.isTestingLogin$,
		this.findQuery$,
		this.findIndex$,
	]).pipe(
		map(([
			isAdmin,
			integrations,
			selectedIntegrationId,
			logins,
			selectedLoginId,
			templates,
			selectedTemplateId,
			collections,
			activeCollectionId,
			activeRequestId,
			requestSearch,
			customVars,
			subTab,
			isSending,
			responseTab,
			lastResponse,
			history,
			_error,
			gridPath,
			gridRows,
			gridError,
			isTestingLogin,
			findQuery,
			findIndex,
		]) => {
			const activeCollection = collections.find(c => c.id === activeCollectionId) ?? null;
			const activeRequest = activeCollection?.requests.find(r => r.id === activeRequestId) ?? null;

			const s = (requestSearch ?? '').trim().toLowerCase();

			// ----- cross-collection search results -----
			const searchResults: Array<{
				colId: string;
				colName: string;
				reqId: string;
				reqName: string;
				reqUrl: string;
				method: string;
			}> = [];

			if (s.length >= 2) {
				for (const c of (collections ?? [])) {
					const colName = (c?.name ?? '').toString();
					const colNameLc = colName.toLowerCase();

					for (const r of (c?.requests ?? [])) {
						const name = (r?.name ?? '').toString();
						const url = (r?.url ?? '').toString();

						const hit =
							colNameLc.includes(s) ||
							name.toLowerCase().includes(s) ||
							url.toLowerCase().includes(s);

						if (!hit) continue;

						searchResults.push({
							colId: c.id,
							colName,
							reqId: r.id,
							reqName: name,
							reqUrl: url,
							method: (r?.method ?? '').toString(),
						});

						if (searchResults.length >= 60) break;
					}

					if (searchResults.length >= 60) break;
				}

				// sort once (not inside the loop)
				searchResults.sort((a, b) =>
					a.colName.localeCompare(b.colName) ||
					a.reqName.localeCompare(b.reqName)
				);
			}

			// ----- keep existing list behavior for active collection -----
			const sTrim = (requestSearch ?? '').toString().trim();
			const sLc = sTrim.toLowerCase();

			const filteredRequests = (activeCollection?.requests ?? []).filter(r => {
				// only filter when user typed 2+ chars
				if (sLc.length < 2) return true;

				const name = (r?.name ?? '').toString().toLowerCase();
				const url = (r?.url ?? '').toString().toLowerCase();
				return name.includes(sLc) || url.includes(sLc);
			});

			const canShowGrid = !!gridRows && gridRows.length > 0;

			return {
				isAdmin,
				integrations,
				selectedIntegrationId,

				logins,
				selectedLoginId,

				templates,
				selectedTemplateId,

				collections,
				activeCollection,
				activeRequest,

				requestSearch,
				filteredRequests,
				searchResults,

				customVars,

				allowedMethods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
				bodyTypes: [
					{ label: 'None', value: 'none' },
					{ label: 'JSON', value: 'json' },
					{ label: 'XML', value: 'xml' },
					{ label: 'Text', value: 'text' },
				],

				subTab,
				isSending,

				responseTab,
				lastResponse,

				history: history ?? [],

				gridPath,
				gridRows,
				gridError,
				canShowGrid,

				isTestingLogin,
				findQuery,
				findIndex,
			} satisfies Vm;
		}),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	constructor(
		private readonly asamcoUserService: AsamcoUserService,
		private readonly applicationService: ApplicationService,
		private readonly adminService: IntegrationTesterAdminService,
		private readonly responseRender: ResponseRenderService,
	) { }

	// --------------------
	// Init / teardown
	// --------------------
	init(): void {
		// theme
		const savedTheme = (localStorage.getItem(IT_THEME_KEY) as ThemeMode) || 'dark';
		this.setTheme(savedTheme);

		// response width
		const savedRespW = Number(localStorage.getItem(IT_RESP_WIDTH_KEY));
		if (Number.isFinite(savedRespW) && savedRespW > 0) this.setResponseWidth(Math.round(savedRespW));

		// last integration
		const lastIntegrationId = safeNumber(localStorage.getItem(IT_LAST_INTEGRATION_KEY));
		if (lastIntegrationId) this.selectedIntegrationId$.next(lastIntegrationId);

		// When integration changes, load collections from DB
		this.selectedIntegrationId$
			.pipe(
				distinctUntilChanged(),
				tap(id => {
					this.selectedLoginId$.next(null);
					this.selectedTemplateId$.next(null);
					this.subTab$.next('params');
					this.responseTab$.next('body');
					this.lastResponse$.next(null);

					if (id) localStorage.setItem(IT_LAST_INTEGRATION_KEY, String(id));

					this.collections$.next([]);
					this.activeCollectionId$.next(null);
					this.activeRequestId$.next(null);
				}),
				switchMap(id => {
					if (!id) return of<CollectionItem[]>([]);
					return this.adminService.getCollections(id).pipe(
						map(dto => this.fromCollectionsDto(dto as any)),
						map(cols => this.ensureTemplatesCollection(cols)),
						catchError(err => {
							this.error$.next(normalizeError(err));
							return of(this.ensureTemplatesCollection([]));
						})
					);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe(cols => {
				this.collections$.next(cols);

				const integrationId = this.selectedIntegrationId$.value;
				const lastId = integrationId
					? (localStorage.getItem(keyLastCollection(integrationId)) ?? null)
					: null;

				const preferredCol =
					(lastId ? cols.find(c => c.id === lastId) : null) ??
					cols.find(c => !this.isTemplatesCollectionId(c.id)) ??
					cols[0] ??
					null;

				this.activeCollectionId$.next(preferredCol?.id ?? null);
				this.activeRequestId$.next(preferredCol?.requests?.[0]?.id ?? null);

				// restore cached response for the active request (survives reloads)
				this.loadCachedResponseForActiveRequest();
			});

		// Auto-save collections to DB (debounced)
		combineLatest([this.selectedIntegrationId$, this.collections$]).pipe(
			filter(([id]) => !!id),
			debounceTime(500),
			switchMap(([id, cols]) => {
				const payload = this.toCollectionsDto(cols);
				return this.adminService.saveCollections(id!, payload as any).pipe(
					catchError(err => {
						this.error$.next(normalizeError(err));
						return of({ ok: false });
					})
				);
			}),
			takeUntil(this.destroy$)
		).subscribe();

		// Save vars/history local
		combineLatest([this.selectedIntegrationId$, this.customVars$])
			.pipe(
				tap(([id, vars]) => { if (id) this.saveVarsLocal(id, vars); }),
				takeUntil(this.destroy$)
			)
			.subscribe();

		combineLatest([this.selectedIntegrationId$, this.history$])
			.pipe(
				tap(([id, hist]) => { if (id) this.saveHistoryLocal(id, hist); }),
				takeUntil(this.destroy$)
			)
			.subscribe();

		// Load vars/history per integration
		this.selectedIntegrationId$
			.pipe(
				distinctUntilChanged(),
				tap(id => {
					const vars = id ? this.loadVarsLocal(id) : [];
					this.customVars$.next(vars);

					const history = id ? this.loadHistoryLocal(id) : [];
					this.history$.next(history);
				}),
				takeUntil(this.destroy$)
			)
			.subscribe();
	}

	dispose(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	// --------------------
	// Theme / width
	// --------------------
	toggleTheme(): void {
		const next: ThemeMode = this.themeSubject.value === 'dark' ? 'light' : 'dark';
		this.setTheme(next);
	}

	setTheme(mode: ThemeMode): void {
		this.themeSubject.next(mode);
		try { localStorage.setItem(IT_THEME_KEY, mode); } catch { }
	}

	setResponseWidth(px: number): void {
		const n = Math.max(280, Math.min(2000, Math.round(px)));
		this.respWidth$.next(n);
		try { localStorage.setItem(IT_RESP_WIDTH_KEY, String(n)); } catch { }
	}

	getResponseWidth(): number {
		const v = this.respWidth$.value;
		if (Number.isFinite(v) && v > 0) return v;
		return 560;
	}

	// --------------------
	// Selectors / setters
	// --------------------
	setIntegration(id: number | null): void { this.selectedIntegrationId$.next(id); }
	setLogin(id: number | null): void { this.selectedLoginId$.next(id); }
	setTemplate(id: string | null): void { this.selectedTemplateId$.next(id); }

	setActiveCollection(id: string | null): void {
		this.activeCollectionId$.next(id);

		const integrationId = this.selectedIntegrationId$.value;
		if (integrationId && id) localStorage.setItem(keyLastCollection(integrationId), id);

		const col = this.collections$.value.find(c => c.id === id);
		this.activeRequestId$.next(col?.requests?.[0]?.id ?? null);

		// restore cached response for that (integration, collection, request)
		this.loadCachedResponseForActiveRequest();
	}

	setActiveRequest(requestId: string): void {
		this.activeRequestId$.next(requestId);

		// restore cached response for that (integration, collection, request)
		this.loadCachedResponseForActiveRequest();
	}


	setRequestSearch(v: string): void { this.requestSearch$.next((v ?? '').toString()); }
	setSubTab(tab: SubTab): void { this.subTab$.next(tab); }

	setResponseTab(tab: ResponseTab): void { this.responseTab$.next(tab); }

	setFindQuery(q: string): void {
		const next = (q ?? '').toString();
		this.findQuery$.next(next);
		this.findIndex$.next(0);
	}

	findPrev(): void { this.bumpFindIndex(-1); }
	findNext(): void { this.bumpFindIndex(+1); }

	private bumpFindIndex(delta: number): void {
		// We don't know match count here without DOM; clamp is done in renderedBodyHtml$
		const cur = this.findIndex$.value;
		this.findIndex$.next(Math.max(0, cur + delta));
	}

	setGridPath(v: string): void { this.gridPath$.next((v ?? '').toString()); }

	// --------------------
	// CRUD: Collections / Requests (pure mutations; Swal should be in shell)
	// --------------------
	createCollection(name: string): void {
		const cols = this.ensureTemplatesCollection(this.collections$.value.slice());
		const col: CollectionItem = {
			id: newId('col'),
			name: name.trim(),
			requests: [this.createDefaultRequest()],
		};
		cols.unshift(col);
		this.collections$.next(cols);
		this.activeCollectionId$.next(col.id);
		this.activeRequestId$.next(col.requests[0].id);
	}

	renameActiveCollection(name: string): void {
		const colId = this.activeCollectionId$.value;
		if (!colId) return;
		const cols = this.collections$.value.slice();
		const idx = cols.findIndex(c => c.id === colId);
		if (idx < 0) return;
		cols[idx] = { ...cols[idx], name: name.trim() };
		this.collections$.next(cols);
	}

	deleteActiveCollection(): void {
		const colId = this.activeCollectionId$.value;
		if (!colId) return;

		const cols = this.collections$.value.filter(c => c.id !== colId);
		const nextCols = this.ensureTemplatesCollection(cols);
		this.collections$.next(nextCols);

		const next =
			nextCols.find(c => !this.isTemplatesCollectionId(c.id)) ??
			nextCols[0] ??
			null;

		this.activeCollectionId$.next(next?.id ?? null);
		this.activeRequestId$.next(next?.requests?.[0]?.id ?? null);
	}

	createRequest(name: string): void {
		const colId = this.activeCollectionId$.value;
		if (!colId) return;

		const cols = this.collections$.value.slice();
		const idx = cols.findIndex(c => c.id === colId);
		if (idx < 0) return;

		const r = this.createDefaultRequest();
		r.name = (name ?? '').toString().trim() || 'New request';

		cols[idx] = { ...cols[idx], requests: [r, ...cols[idx].requests] };
		this.collections$.next(cols);
		this.activeRequestId$.next(r.id);
	}

	duplicateActiveRequest(): void {
		const colId = this.activeCollectionId$.value;
		const reqId = this.activeRequestId$.value;
		if (!colId || !reqId) return;

		const cols = this.collections$.value.slice();
		const colIdx = cols.findIndex(c => c.id === colId);
		if (colIdx < 0) return;

		const req = cols[colIdx].requests.find(r => r.id === reqId);
		if (!req) return;

		const copy: RequestItem = {
			...deepClone(req),
			id: newId('req'),
			name: `${req.name} (copy)`,
		};

		cols[colIdx] = { ...cols[colIdx], requests: [copy, ...cols[colIdx].requests] };
		this.collections$.next(cols);
		this.activeRequestId$.next(copy.id);
	}

	deleteActiveRequest(): void {
		const colId = this.activeCollectionId$.value;
		const reqId = this.activeRequestId$.value;
		if (!colId || !reqId) return;

		const cols = this.collections$.value.slice();
		const colIdx = cols.findIndex(c => c.id === colId);
		if (colIdx < 0) return;

		const reqs = cols[colIdx].requests.filter(r => r.id !== reqId);
		cols[colIdx] = { ...cols[colIdx], requests: reqs };
		this.collections$.next(cols);

		this.activeRequestId$.next(reqs[0]?.id ?? null);
	}

	updateActiveRequest(patch: Partial<RequestItem>): void {
		const colId = this.activeCollectionId$.value;
		const reqId = this.activeRequestId$.value;
		if (!colId || !reqId) return;

		const cols = this.collections$.value.slice();
		const colIdx = cols.findIndex(c => c.id === colId);
		if (colIdx < 0) return;

		const reqs = cols[colIdx].requests.slice();
		const reqIdx = reqs.findIndex(r => r.id === reqId);
		if (reqIdx < 0) return;

		reqs[reqIdx] = { ...reqs[reqIdx], ...patch };
		cols[colIdx] = { ...cols[colIdx], requests: reqs };
		this.collections$.next(cols);
	}

	// Params / Headers edit helpers (UI can also do it)
	addParam(): void {
		const r = this.getActiveRequest();
		if (!r) return;
		this.updateActiveRequest({ params: [...(r.params ?? []), { key: '', value: '' }] });
	}
	updateParam(i: number, patch: Partial<Kv>): void {
		const r = this.getActiveRequest();
		if (!r) return;
		const arr = (r.params ?? []).slice();
		arr[i] = { ...arr[i], ...patch };
		this.updateActiveRequest({ params: arr });
	}
	removeParam(i: number): void {
		const r = this.getActiveRequest();
		if (!r) return;
		const arr = (r.params ?? []).slice();
		arr.splice(i, 1);
		this.updateActiveRequest({ params: arr });
	}
	clearParams(): void { this.updateActiveRequest({ params: [] }); }

	addHeader(): void {
		const r = this.getActiveRequest();
		if (!r) return;
		this.updateActiveRequest({ headers: [...(r.headers ?? []), { key: '', value: '' }] });
	}
	addCommonHeaders(): void {
		const r = this.getActiveRequest();
		if (!r) return;
		const common: Kv[] = [{ key: 'Accept', value: 'application/json' }];
		const merged = mergeKv(r.headers ?? [], common);
		this.updateActiveRequest({ headers: merged });
	}
	updateHeader(i: number, patch: Partial<Kv>): void {
		const r = this.getActiveRequest();
		if (!r) return;
		const arr = (r.headers ?? []).slice();
		arr[i] = { ...arr[i], ...patch };
		this.updateActiveRequest({ headers: arr });
	}
	removeHeader(i: number): void {
		const r = this.getActiveRequest();
		if (!r) return;
		const arr = (r.headers ?? []).slice();
		arr.splice(i, 1);
		this.updateActiveRequest({ headers: arr });
	}
	clearHeaders(): void { this.updateActiveRequest({ headers: [] }); }

	// Vars (kept as state)
	addVar(): void { this.customVars$.next([...(this.customVars$.value ?? []), { key: '', value: '' }]); }
	updateVar(i: number, patch: Partial<Kv>): void {
		const vars = (this.customVars$.value ?? []).slice();
		vars[i] = { ...vars[i], ...patch };
		this.customVars$.next(vars);
	}
	removeVar(i: number): void {
		const vars = (this.customVars$.value ?? []).slice();
		vars.splice(i, 1);
		this.customVars$.next(vars);
	}
	clearVars(): void { this.customVars$.next([]); }

	// --------------------
	// Drag/drop (wire your UI to these)
	// --------------------
	reorderRequestWithinCollection(colId: string, movingReqId: string, targetReqId: string, place: 'above' | 'below'): void {
		const cols = (this.collections$.value ?? []).slice();
		const colIdx = cols.findIndex(c => c.id === colId);
		if (colIdx < 0) return;

		const reqs = (cols[colIdx].requests ?? []).slice();
		const fromIdx = reqs.findIndex(r => r.id === movingReqId);
		const targetIdx = reqs.findIndex(r => r.id === targetReqId);
		if (fromIdx < 0 || targetIdx < 0) return;

		const [moving] = reqs.splice(fromIdx, 1);
		const targetIdxAfterRemoval = fromIdx < targetIdx ? targetIdx - 1 : targetIdx;

		const insertIdx = place === 'above' ? targetIdxAfterRemoval : targetIdxAfterRemoval + 1;
		reqs.splice(Math.max(0, Math.min(insertIdx, reqs.length)), 0, moving);

		cols[colIdx] = { ...cols[colIdx], requests: reqs };
		this.collections$.next(cols);
	}

	moveRequestToEndOfCollection(colId: string, reqId: string): void {
		const cols = (this.collections$.value ?? []).slice();
		const colIdx = cols.findIndex(c => c.id === colId);
		if (colIdx < 0) return;

		const reqs = (cols[colIdx].requests ?? []).slice();
		const fromIdx = reqs.findIndex(r => r.id === reqId);
		if (fromIdx < 0) return;

		const [moving] = reqs.splice(fromIdx, 1);
		reqs.push(moving);

		cols[colIdx] = { ...cols[colIdx], requests: reqs };
		this.collections$.next(cols);
	}

	moveRequestToCollection(fromColId: string, toColId: string, reqId: string): void {
		const cols = (this.collections$.value ?? []).slice();
		const fromIdx = cols.findIndex(c => c.id === fromColId);
		const toIdx = cols.findIndex(c => c.id === toColId);
		if (fromIdx < 0 || toIdx < 0) return;

		const fromReqs = (cols[fromIdx].requests ?? []).slice();
		const reqIndex = fromReqs.findIndex(r => r.id === reqId);
		if (reqIndex < 0) return;

		const [moving] = fromReqs.splice(reqIndex, 1);
		const toReqs = (cols[toIdx].requests ?? []).slice();
		toReqs.unshift(moving);

		cols[fromIdx] = { ...cols[fromIdx], requests: fromReqs };
		cols[toIdx] = { ...cols[toIdx], requests: toReqs };
		this.collections$.next(cols);

		if (this.activeCollectionId$.value === fromColId && this.activeRequestId$.value === reqId) {
			this.activeRequestId$.next(fromReqs[0]?.id ?? null);
		}
	}

	// --------------------
	// Body helpers
	// --------------------
	clearBody(): void { this.updateActiveRequest({ body: '' }); }

	detectModeForBody(bodyType: BodyType, trimmed: string): 'json' | 'xml' | 'text' {
		if (bodyType === 'json') return 'json';
		if (bodyType === 'xml') return 'xml';
		if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']')))
			return 'json';
		if (trimmed.startsWith('<') && trimmed.endsWith('>')) return 'xml';
		return 'text';
	}

	formatBody(): { ok: boolean; error?: string } {
		const r = this.getActiveRequest();
		if (!r) return { ok: false, error: 'No active request.' };

		const txt = (r.body ?? '').toString();
		const trimmed = txt.trim();
		if (!trimmed) return { ok: true };

		const mode = this.detectModeForBody(r.bodyType, trimmed);

		try {
			if (mode === 'json') {
				const obj = JSON.parse(trimmed);
				const pretty = JSON.stringify(obj, null, 2);
				this.updateActiveRequest({ body: pretty });
				return { ok: true };
			}
			if (mode === 'xml') {
				const pretty = prettyXml(trimmed);
				this.updateActiveRequest({ body: pretty });
				return { ok: true };
			}
			return { ok: false, error: 'Body is not JSON or XML.' };
		} catch (err: any) {
			return { ok: false, error: err?.message ?? String(err) };
		}
	}

	getEffectiveBodyContentType(r: RequestItem | null | undefined): string {
		if (!r) return '';
		const explicit = (r.contentType ?? '').trim();
		if (explicit) return explicit;

		const trimmed = (r.body ?? '').toString().trim();

		if (r.bodyType === 'json') return 'application/json';
		if (r.bodyType === 'xml') return 'text/xml';
		if (r.bodyType === 'text') return 'text/plain';

		if (!trimmed) return '';
		if (isJsonLike(trimmed)) return 'application/json';
		if (trimmed.startsWith('<') && trimmed.endsWith('>')) return 'text/xml';
		return 'text/plain';
	}

	// --------------------
	// Send / test login
	// --------------------
	testLogin(): Observable<void> {
		const integrationId = this.selectedIntegrationId$.value;
		const loginId = this.selectedLoginId$.value;
		if (!integrationId || !loginId) return EMPTY;

		this.isTestingLogin$.next(true);

		return this.adminService.testLogin(integrationId, loginId).pipe(
			map(() => void 0),
			catchError(err => {
				// Keep your state channel (normalized)
				this.error$.next(normalizeError(err));

				// IMPORTANT: propagate the ORIGINAL error unchanged
				return throwError(() => err);
			}),
			finalize(() => this.isTestingLogin$.next(false))
		);
	}

	async send(): Promise<void> {
		const integrationId = this.selectedIntegrationId$.value;
		const loginId = this.selectedLoginId$.value;
		const r = this.getActiveRequest();

		if (!integrationId || !loginId || !r) return;

		// capture the target request identity at send-time (so caching is correct even if user clicks fast)
		const colIdAtSend = this.activeCollectionId$.value;
		const reqIdAtSend = this.activeRequestId$.value;


		if (!integrationId || !loginId || !r) return;

		const vars = this.buildVarsMap(integrationId, loginId, this.customVars$.value);

		const resolvedUrl = this.applyVars(r.url, vars);

		const resolvedParams = (r.params ?? [])
			.filter(x => (x.key ?? '').trim().length > 0)
			.map(x => ({ key: this.applyVars(x.key, vars), value: this.applyVars(x.value, vars) }));

		const resolvedHeaders = (r.headers ?? [])
			.filter(x => (x.key ?? '').trim().length > 0)
			.map(x => ({ key: this.applyVars(x.key, vars), value: this.applyVars(x.value, vars) }));

		const resolvedBody = this.applyVars(r.body ?? '', vars);
		const urlOrPath = this.appendQueryString(resolvedUrl, resolvedParams);

		const methodUpper = ((r.method ?? 'GET') as string).toUpperCase();
		const canHaveBody =
			methodUpper === 'POST' || methodUpper === 'PUT' || methodUpper === 'PATCH' || methodUpper === 'DELETE';

		const trimmedBody = (resolvedBody ?? '').toString();
		const bodyToSend = (canHaveBody && trimmedBody.trim().length > 0) ? trimmedBody : null;

		const contentType = bodyToSend ? this.getEffectiveBodyContentType(r) : '';

		const headerDict: Record<string, string> = {};
		for (const h of resolvedHeaders) {
			const k = (h.key ?? '').toString().trim();
			if (!k) continue;
			headerDict[k] = (h.value ?? '').toString();
		}
		if (contentType) headerDict['Content-Type'] = contentType;

		const reqDto: SendRequestDto = {
			method: methodUpper,
			urlOrPath,
			headers: Object.keys(headerDict).length ? headerDict : null,
			body: bodyToSend,
			contentType: contentType || null,
			target: null,
			timeoutSeconds: 60,
		};

		const payload: SendEnvelopeDto = {
			applicationLoginId: loginId,
			request: reqDto,
		};

		this.isSending$.next(true);
		this.lastResponse$.next(null);


		const started = performance.now();

		try {
			const resp = await this.adminService.sendRaw(integrationId, payload).toPromise();
			const durationMs = Math.max(0, Math.round(performance.now() - started));
			const parsed = this.normalizeControllerSendResponse(resp, durationMs);

			this.lastResponse$.next(parsed);
			this.responseTab$.next('body');

			// cache it (integrationId + collectionId + requestId)
			if (colIdAtSend && reqIdAtSend) {
				this.cachePutResponse(integrationId, colIdAtSend, reqIdAtSend, parsed);
			}

			this.pushHistory({
				at: Date.now(),
				method: r.method,
				url: urlOrPath,
				status: parsed.status,
				durationMs: parsed.durationMs,
				requestSnapshot: deepClone(r),
			});
		} catch (err: any) {
			const durationMs = Math.max(0, Math.round(performance.now() - started));
			const vmErr = this.toExecuteResponseFromHttpError(err, durationMs);

			this.lastResponse$.next(vmErr);
			this.responseTab$.next('body');

			// cache errors too (useful)
			if (colIdAtSend && reqIdAtSend) {
				this.cachePutResponse(integrationId, colIdAtSend, reqIdAtSend, vmErr);
			}

			this.pushHistory({
				at: Date.now(),
				method: r.method,
				url: urlOrPath,
				status: vmErr.status,
				durationMs: vmErr.durationMs,
				requestSnapshot: deepClone(r),
			});
		} finally {
			this.isSending$.next(false);
		}
	}

	copyResponseBody(): void {
		const r = this.lastResponse$.value;
		if (!r) return;
		const txt = (r.bodyText ?? '').toString();
		if (!txt) return;
		navigator.clipboard.writeText(txt);
	}

	// --------------------
	// Templates (still pure)
	// --------------------
	applyTemplate(templateId: string): void {
		const r = this.getActiveRequest();
		if (!r) return;

		const cols = this.collections$.value ?? [];
		const tcol = this.findTemplatesCollection(cols);
		const t = tcol?.requests.find(x => x.id === templateId);
		if (!t) return;

		this.updateActiveRequest({
			method: t.method,
			url: t.url,
			params: deepClone(t.params ?? []),
			headers: deepClone(t.headers ?? []),
			bodyType: t.bodyType,
			contentType: t.contentType,
			body: t.body ?? '',
		});
	}

	createTemplateFromActiveRequest(name: string): void {
		const r = this.getActiveRequest();
		if (!r) return;

		const cols = this.ensureTemplatesCollection((this.collections$.value ?? []).slice());
		const tcol = this.findTemplatesCollection(cols);
		if (!tcol) return;

		const newReq: RequestItem = {
			...deepClone(r),
			id: newId('tpl'),
			name: name.trim(),
		};

		const updatedCol: CollectionItem = { ...tcol, requests: [newReq, ...(tcol.requests ?? [])] };
		const nextCols = cols.map(c => (c.id === tcol.id ? updatedCol : c));

		this.collections$.next(nextCols);
		this.selectedTemplateId$.next(newReq.id);
	}

	renameTemplate(templateId: string, name: string): void {
		const cols = (this.collections$.value ?? []).slice();
		const tcol = this.findTemplatesCollection(cols);
		if (!tcol) return;

		const idx = (tcol.requests ?? []).findIndex(x => x.id === templateId);
		if (idx < 0) return;

		const reqs = tcol.requests.slice();
		reqs[idx] = { ...reqs[idx], name: name.trim() };

		const nextCols = cols.map(c => (c.id === tcol.id ? { ...tcol, requests: reqs } : c));
		this.collections$.next(nextCols);
	}

	deleteTemplate(templateId: string): void {
		const cols = (this.collections$.value ?? []).slice();
		const tcol = this.findTemplatesCollection(cols);
		if (!tcol) return;

		const reqs = (tcol.requests ?? []).filter(x => x.id !== templateId);
		const nextCols = cols.map(c => (c.id === tcol.id ? { ...tcol, requests: reqs } : c));
		this.collections$.next(nextCols);

		if (this.selectedTemplateId$.value === templateId) this.selectedTemplateId$.next(null);
	}

	// --------------------
	// Internal helpers
	// --------------------
	private getActiveRequest(): RequestItem | null {
		const colId = this.activeCollectionId$.value;
		const reqId = this.activeRequestId$.value;
		if (!colId || !reqId) return null;
		const col = this.collections$.value.find(c => c.id === colId);
		return col?.requests.find(r => r.id === reqId) ?? null;
	}

	private createDefaultRequest(): RequestItem {
		return {
			id: newId('req'),
			name: 'New request',
			method: 'GET',
			url: '',
			params: [],
			headers: [],
			bodyType: 'none',
			body: '',
		};
	}

	private buildVarsMap(integrationId: number, loginId: number, custom: Kv[]): Record<string, string> {
		const vars: Record<string, string> = {
			applicationId: String(integrationId),
			loginId: String(loginId),
			baseUrl: '',
		};
		for (const kv of custom ?? []) {
			const k = (kv.key ?? '').trim();
			if (!k) continue;
			vars[k] = (kv.value ?? '').toString();
		}
		return vars;
	}

	private applyVars(text: string, vars: Record<string, string>): string {
		const s = (text ?? '').toString();
		return s.replace(/\{\{\s*([a-zA-Z0-9_\-\.]+)\s*\}\}/g, (_, key) => {
			const k = (key ?? '').toString();
			return Object.prototype.hasOwnProperty.call(vars, k) ? (vars[k] ?? '') : '';
		});
	}

	private appendQueryString(url: string, params: Array<{ key: string; value: string }>): string {
		const base = (url ?? '').toString();
		if (!params || params.length === 0) return base;

		const parts: string[] = [];
		for (const p of params) {
			const k = (p.key ?? '').toString().trim();
			if (!k) continue;
			const v = (p.value ?? '').toString();
			parts.push(`${encodeURIComponent(k)}=${encodeURIComponent(v)}`);
		}

		if (parts.length === 0) return base;
		return base.includes('?') ? `${base}&${parts.join('&')}` : `${base}?${parts.join('&')}`;
	}

	private pushHistory(item: HistoryItem): void {
		const hist = (this.history$.value ?? []).slice();
		hist.unshift(item);
		this.history$.next(hist.slice(0, 30));
	}

	private normalizeControllerSendResponse(resp: any, fallbackDurationMs: number): ExecuteResponseVm {
		const status = safeNumber(resp?.status) || 0;

		const headers: Record<string, string> = {};
		const src = resp?.headers ?? {};
		for (const k of Object.keys(src)) {
			headers[k] = Array.isArray(src[k]) ? src[k].join(', ') : (src[k] ?? '').toString();
		}

		const headersText = Object.keys(headers)
			.sort((a, b) => a.localeCompare(b))
			.map(k => `${k}: ${headers[k]}`)
			.join('\n');

		const bodyText = (resp?.body ?? '').toString();

		return {
			status,
			durationMs: fallbackDurationMs,
			headers,
			headersText,
			bodyText,
			bodyJson: null,
			gridRows: null,
		};
	}

	private toExecuteResponseFromHttpError(err: any, durationMs: number): ExecuteResponseVm {
		const fallback: ExecuteResponseVm = {
			status: 0,
			durationMs,
			headers: {},
			headersText: '',
			bodyText: normalizeError(err),
			bodyJson: null,
			gridRows: null,
		};

		const httpErr = err as HttpErrorResponse;
		if (!httpErr || typeof httpErr.status !== 'number') return fallback;

		const headers = httpHeadersToDict(httpErr.headers);
		const headersText = headersDictToText(headers);
		const bodyText = this.extractErrorBodyText(httpErr);

		return {
			status: httpErr.status || 0,
			durationMs,
			headers,
			headersText,
			bodyText,
			bodyJson: null,
			gridRows: null,
		};
	}

	private extractErrorBodyText(httpErr: HttpErrorResponse): string {
		const e = httpErr.error as any;

		if (typeof e === 'string') return e;
		if (e && typeof e === 'object' && !(e instanceof Blob)) {
			try { return JSON.stringify(e, null, 2); } catch { return String(e); }
		}
		if (e instanceof Blob) {
			return `[binary error body] (${e.type || 'application/octet-stream'}, ${e.size} bytes)`;
		}
		return (httpErr.message ?? 'Request failed');
	}

	// --------------------
	// Templates collection marker
	// --------------------
	private isTemplatesCollectionId(id: string | null | undefined): boolean {
		return !!id && id.startsWith(IT_TEMPLATES_ID_PREFIX);
	}

	private createTemplatesCollectionId(): string {
		return `${IT_TEMPLATES_ID_PREFIX}${newId('col')}`;
	}

	private findTemplatesCollection(cols: CollectionItem[] | null | undefined): CollectionItem | null {
		const list = Array.isArray(cols) ? cols : [];
		return list.find(c => this.isTemplatesCollectionId(c.id)) ?? null;
	}

	private ensureTemplatesCollection(cols: CollectionItem[]): CollectionItem[] {
		const safe = Array.isArray(cols) ? cols.slice() : [];
		const exists = safe.some(c => this.isTemplatesCollectionId(c.id));
		if (exists) return safe;

		const templateCol: CollectionItem = {
			id: this.createTemplatesCollectionId(),
			name: IT_TEMPLATES_COLLECTION_NAME,
			requests: [],
		};
		return [templateCol, ...safe];
	}

	// --------------------
	// DTO mapping
	// --------------------
	private toCollectionsDto(cols: CollectionItem[]): ItCollectionDto[] {
		return (cols ?? []).map((c, colIndex) => ({
			id: c.id,
			name: c.name,
			sortOrder: colIndex,
			requests: (c.requests ?? []).map((r, reqIndex) => ({
				id: r.id,
				name: r.name,
				description: null,
				method: r.method,
				url: r.url,
				params: r.params ?? [],
				headers: r.headers ?? [],
				bodyType: r.bodyType,
				contentType: r.contentType ?? null,
				body: r.body ?? '',
				sortOrder: reqIndex,
			})),
		})) as any;
	}

	private fromCollectionsDto(cols: ItCollectionDto[]): CollectionItem[] {
		const safe = Array.isArray(cols) ? cols : [];
		return safe.map(c => ({
			id: (c?.id ?? '').toString(),
			name: (c?.name ?? '').toString(),
			requests: (Array.isArray(c?.requests) ? c.requests : []).map(r => ({
				id: (r?.id ?? '').toString(),
				name: (r?.name ?? '').toString(),
				method: ((r?.method ?? 'GET').toString().toUpperCase() as HttpMethod),
				url: (r?.url ?? '').toString(),
				params: Array.isArray(r?.params) ? r.params : [],
				headers: Array.isArray(r?.headers) ? r.headers : [],
				bodyType: ((r?.bodyType ?? 'none').toString() as BodyType),
				contentType: (r?.contentType ?? undefined) as any,
				body: (r?.body ?? '').toString(),
			})),
		}));
	}

	// --------------------
	// XML parse (same as your original)
	// --------------------
	private parseXmlToObject(xmlText: string): any {
		const txt = (xmlText ?? '').toString().trim();
		if (!txt) return null;

		const doc = new DOMParser().parseFromString(txt, 'application/xml');
		const parseError = doc.getElementsByTagName('parsererror')?.[0];
		if (parseError) throw new Error((parseError.textContent ?? 'Invalid XML').trim());

		const root = doc.documentElement;
		return { [root.nodeName]: this.xmlElementToObject(root) };
	}

	private xmlElementToObject(el: Element): any {
		const obj: any = {};

		if (el.attributes?.length) {
			const attrs: any = {};
			for (let i = 0; i < el.attributes.length; i++) {
				const a = el.attributes.item(i);
				if (!a) continue;
				attrs[a.name] = a.value;
			}
			obj['@'] = attrs;
		}

		const childEls = Array.from(el.children ?? []);
		if (childEls.length) {
			const grouped = new Map<string, Element[]>();
			for (const c of childEls) {
				const key = c.nodeName;
				const arr = grouped.get(key) ?? [];
				arr.push(c);
				grouped.set(key, arr);
			}

			for (const [name, list] of grouped.entries()) {
				if (list.length === 1) obj[name] = this.xmlElementToObject(list[0]);
				else obj[name] = list.map(x => this.xmlElementToObject(x));
			}
		}

		const text = (el.textContent ?? '').trim();
		const hasElementChildren = childEls.length > 0;

		if (!hasElementChildren) {
			if (!obj['@']) return this.coerceScalar(text);
			obj['#text'] = this.coerceScalar(text);
		}

		return obj;
	}

	private coerceScalar(v: string): any {
		const s = (v ?? '').trim();
		if (!s) return '';
		if (s === 'true') return true;
		if (s === 'false') return false;
		const n = Number(s);
		if (Number.isFinite(n) && String(n) === s) return n;
		return s;
	}

	// --------------------
	// Path tree + grid stringify (same spirit as yours)
	// --------------------
	private buildPathTree(obj: any): PathNode[] {
		if (obj == null) return [];
		return this.makeChildren(obj, '', 0);
	}

	private makeChildren(value: any, basePath: string, depth: number): PathNode[] {
		const MAX_DEPTH = 10;
		const MAX_CHILDREN = 200;
		if (depth > MAX_DEPTH) return [];

		if (Array.isArray(value)) {
			const label = basePath ? this.lastSeg(basePath) : '(array)';
			const node: PathNode = {
				id: newTreeId(),
				text: `${label} [ ] (${value.length})`,
				path: basePath,
				isArray: true,
				items: [],
			};

			const first = value[0];
			if (first && typeof first === 'object' && depth < MAX_DEPTH) {
				node.items = this.makeChildren(first, basePath + '[0]', depth + 1);
			}
			return [node];
		}

		if (value && typeof value === 'object') {
			const keys = Object.keys(value).slice(0, MAX_CHILDREN);
			return keys.map(k => {
				const childVal = value[k];
				const childPath = basePath ? `${basePath}.${k}` : k;

				const isArr = Array.isArray(childVal);
				const isObj = childVal && typeof childVal === 'object' && !isArr;

				const summary = isArr
					? `[ ] (${childVal.length})`
					: isObj
						? `{ }`
						: this.previewScalar(childVal);

				const node: PathNode = {
					id: newTreeId(),
					text: `${k} ${summary}`,
					path: childPath,
					isArray: isArr,
					items: [],
				};

				if ((isArr || isObj) && depth < MAX_DEPTH) {
					node.items = this.makeChildren(childVal, childPath, depth + 1);
				}
				return node;
			});
		}

		return [];
	}

	private previewScalar(v: any): string {
		if (v == null) return '(null)';
		const s = String(v);
		return s.length > 28 ? `"${s.slice(0, 28)}..."` : `"${s}"`;
	}

	private lastSeg(path: string): string {
		const p = (path ?? '').toString();
		const parts = p.split('.');
		return parts[parts.length - 1] ?? p;
	}

	private stringifyNestedRows(rows: any[]): any[] {
		const list = Array.isArray(rows) ? rows : [];
		return list.map(r => this.stringifyNestedRow(r));
	}

	private stringifyNestedRow(row: any): any {
		if (row == null || typeof row !== 'object') return row;
		if (Array.isArray(row)) {
			return row.map(v => {
				if (v == null) return v;
				if (typeof v === 'object') return JSON.stringify(v, null, 2);
				return v;
			});
		}

		const out: any = {};
		for (const k of Object.keys(row)) {
			const v = row[k];
			if (v == null) { out[k] = v; continue; }
			if (typeof v === 'object') {
				try { out[k] = JSON.stringify(v, null, 2); } catch { out[k] = String(v); }
			} else out[k] = v;
		}
		return out;
	}

	// --------------------
	// Find/highlight helpers (HTML)
	// --------------------
	private tryPrettyJson(text: string): { text: string; isJson: boolean } {
		const t = (text ?? '').toString();
		const trimmed = t.trim();
		if (!trimmed) return { text: '', isJson: false };

		if (isJsonLike(trimmed)) {
			try {
				const obj = JSON.parse(trimmed);
				return { text: JSON.stringify(obj, null, 2), isJson: true };
			} catch { /* fall through */ }
		}
		return { text: t, isJson: false };
	}

	private highlightTextNoActive(source: string, query: string, maxMatches = 2000): { html: string; matches: number } {
		const src = (source ?? '').toString();
		const q = (query ?? '').toString();
		if (!src || !q) return { html: this.escapeHtml(src), matches: 0 };

		const hay = src.toLowerCase();
		const needle = q.toLowerCase();

		const parts: string[] = [];
		let pos = 0;
		let count = 0;

		while (count < maxMatches) {
			const hit = hay.indexOf(needle, pos);
			if (hit === -1) break;

			parts.push(this.escapeHtml(src.slice(pos, hit)));

			const matchText = src.slice(hit, hit + q.length);
			parts.push(`<mark class="it-mark" data-mi="${count}">${this.escapeHtml(matchText)}</mark>`);

			pos = hit + q.length;
			count++;
		}

		parts.push(this.escapeHtml(src.slice(pos)));
		return { html: parts.join(''), matches: count };
	}

	private setActiveMark(htmlWithMarks: string, activeIndex: number): string {
		const n = Math.max(0, Math.floor(activeIndex));
		const needle = `<mark class="it-mark" data-mi="${n}">`;
		const active = `<mark class="it-mark is-active" data-mi="${n}">`;
		return htmlWithMarks.replace(needle, active);
	}

	private preserveIndentAndBr(htmlWithMarksOrEscaped: string): string {
		const lines = (htmlWithMarksOrEscaped ?? '').split('\n');
		for (let i = 0; i < lines.length; i++) {
			lines[i] = lines[i].replace(/^ +/, m => '&nbsp;'.repeat(m.length));
			if (lines[i] === '') lines[i] = '&nbsp;';
		}
		return lines.join('<br>');
	}


	private highlightText(
		source: string,
		query: string,
		activeIndex: number
	): { html: string; matches: number; clampedIndex: number } {
		const src = (source ?? '').toString();
		const q = (query ?? '').toString();
		if (!src || !q) return { html: this.escapeHtml(src), matches: 0, clampedIndex: 0 };

		const hay = src.toLowerCase();
		const needle = q.toLowerCase();
		const MAX_MATCHES = 2000;

		const parts: string[] = [];
		let pos = 0;
		let count = 0;

		while (count < MAX_MATCHES) {
			const hit = hay.indexOf(needle, pos);
			if (hit === -1) break;

			parts.push(this.escapeHtml(src.slice(pos, hit)));

			const matchText = src.slice(hit, hit + q.length);
			parts.push(
				`<mark class="it-mark" data-mi="${count}">${this.escapeHtml(matchText)}</mark>`
			);

			pos = hit + q.length;
			count++;
		}

		parts.push(this.escapeHtml(src.slice(pos)));

		const matches = count;
		const clampedIndex = matches ? Math.max(0, Math.min(activeIndex, matches - 1)) : 0;

		let joined = parts.join('');

		if (matches) {
			// Turn exactly one mark into the active one by editing the existing class attribute.
			const needleMark = `<mark class="it-mark" data-mi="${clampedIndex}">`;
			const activeMark = `<mark class="it-mark is-active" data-mi="${clampedIndex}">`;
			joined = joined.replace(needleMark, activeMark);
		}

		return { html: joined, matches, clampedIndex };
	}

	// --------------------
	// Local storage (vars/history)
	// --------------------
	private loadVarsLocal(integrationId: number): Kv[] {
		try {
			const raw = localStorage.getItem(keyVars(integrationId));
			if (!raw) return [];
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}

	private saveVarsLocal(integrationId: number, vars: Kv[]): void {
		try { localStorage.setItem(keyVars(integrationId), JSON.stringify(vars ?? [])); } catch { }
	}

	private loadHistoryLocal(integrationId: number): HistoryItem[] {
		try {
			const raw = localStorage.getItem(keyHistory(integrationId));
			if (!raw) return [];
			const parsed = JSON.parse(raw);
			return Array.isArray(parsed) ? parsed : [];
		} catch {
			return [];
		}
	}

	private saveHistoryLocal(integrationId: number, history: HistoryItem[]): void {
		try { localStorage.setItem(keyHistory(integrationId), JSON.stringify(history ?? [])); } catch { }
	}

	// --------------------
	// Public helpers for UI
	// --------------------
	isJsonLike(text: string | null | undefined): boolean { return isJsonLike(text); }
	isXmlLike(text: string | null | undefined): boolean { return isXmlLike(text); }

	navigateTo(colId: string, reqId: string): void {
		// switch collection first (this resets activeRequest to first request in that collection)
		this.setActiveCollection(colId);

		// then set the requested request
		this.setActiveRequest(reqId);

		// optional: clear filter after jumping
		this.setRequestSearch('');
	}

	private readonly respCacheStorage: Storage | null = localStorage; // survives reloads
	private readonly respCachePrefix = 'it.respcache.v1.';
	private readonly respCacheMaxEntries = 120;

	private respCacheKey(integrationId: number, collectionId: string, requestId: string): string {
		return `${this.respCachePrefix}${integrationId}|${collectionId}|${requestId}`;
	}

	private cachePutResponse(integrationId: number, collectionId: string, requestId: string, resp: ExecuteResponseVm): void {
		if (!integrationId || !collectionId || !requestId || !resp) return;

		const key = this.respCacheKey(integrationId, collectionId, requestId);
		const entry = { at: Date.now(), resp };

		try {
			this.respCacheStorage?.setItem(key, JSON.stringify(entry));
			this.evictOldResponseCacheEntries();
		} catch {
			// ignore quota / blocked storage
		}
	}

	private cacheGetResponse(integrationId: number, collectionId: string, requestId: string): ExecuteResponseVm | null {
		if (!integrationId || !collectionId || !requestId) return null;

		const key = this.respCacheKey(integrationId, collectionId, requestId);
		const raw = this.respCacheStorage?.getItem(key);
		if (!raw) return null;

		try {
			const parsed = JSON.parse(raw) as { at?: number; resp?: ExecuteResponseVm };
			return parsed?.resp ?? null;
		} catch {
			try { this.respCacheStorage?.removeItem(key); } catch { }
			return null;
		}
	}

	private evictOldResponseCacheEntries(): void {
		const st = this.respCacheStorage;
		if (!st) return;

		try {
			const keys: string[] = [];
			for (let i = 0; i < st.length; i++) {
				const k = st.key(i);
				if (k && k.startsWith(this.respCachePrefix)) keys.push(k);
			}

			if (keys.length <= this.respCacheMaxEntries) return;

			const entries: Array<{ key: string; at: number }> = [];
			for (const k of keys) {
				const raw = st.getItem(k);
				if (!raw) continue;
				try {
					const parsed = JSON.parse(raw) as { at?: number };
					entries.push({ key: k, at: Number(parsed?.at) || 0 });
				} catch {
					// corrupt => remove it
					try { st.removeItem(k); } catch { }
				}
			}

			entries.sort((a, b) => a.at - b.at);

			const removeCount = Math.max(0, entries.length - this.respCacheMaxEntries);
			for (let i = 0; i < removeCount; i++) {
				try { st.removeItem(entries[i].key); } catch { }
			}
		} catch {
			// ignore storage errors
		}
	}

	// Public API for UI button
	clearResponseCache(): void {
		const st = this.respCacheStorage;
		if (st) {
			try {
				const toRemove: string[] = [];
				for (let i = 0; i < st.length; i++) {
					const k = st.key(i);
					if (k && k.startsWith(this.respCachePrefix)) toRemove.push(k);
				}
				for (const k of toRemove) st.removeItem(k);
			} catch {
				// ignore
			}
		}

		// Immediately clear current UI state (this is what makes it update without refresh)
		this.lastResponse$.next(null);
		this.responseTab$.next('body');

		// optional but usually desired
		this.findQuery$.next('');
		this.findIndex$.next(0);
		this.gridPath$.next('');
		this.gridError$.next(null);
	}

	private loadCachedResponseForActiveRequest(): void {
		const integrationId = this.selectedIntegrationId$.value;
		const colId = this.activeCollectionId$.value;
		const reqId = this.activeRequestId$.value;
		if (!integrationId || !colId || !reqId) return;

		const cached = this.cacheGetResponse(integrationId, colId, reqId);
		if (cached) this.lastResponse$.next(cached);
		else this.lastResponse$.next(null);
	}

	// Optional: clear only the current integration’s cache
	clearResponseCacheForCurrentIntegration(): void {
		const st = this.respCacheStorage;
		const integrationId = this.selectedIntegrationId$.value;
		if (!st || !integrationId) return;

		const prefix = `${this.respCachePrefix}${integrationId}|`;

		try {
			const toRemove: string[] = [];
			for (let i = 0; i < st.length; i++) {
				const k = st.key(i);
				if (k && k.startsWith(prefix)) toRemove.push(k);
			}
			for (const k of toRemove) st.removeItem(k);
		} catch {
			// ignore
		}

		this.lastResponse$.next(null);
		this.responseTab$.next('body');
	}

	// Optional: clear only the active request’s cached response
	clearResponseCacheForActiveRequest(): void {
		const st = this.respCacheStorage;
		const integrationId = this.selectedIntegrationId$.value;
		const colId = this.activeCollectionId$.value;
		const reqId = this.activeRequestId$.value;
		if (!st || !integrationId || !colId || !reqId) return;

		const key = `${this.respCachePrefix}${integrationId}|${colId}|${reqId}`;

		try { st.removeItem(key); } catch { }

		this.lastResponse$.next(null);
		this.responseTab$.next('body');
	}

}
